import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Button,
  IconButton,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { ArrowBack, Delete } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar.tsx';
import { useAuth } from '../context/AuthContext.tsx';

const API_BASE = process.env.REACT_APP_API_URL + '/api';



const VerpleegkundigeDetail: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [rol, setRol] = useState("nurse");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    voornaam: "",
    naam: "",
    afkorting: "",
    straatnaam: "",
    nummer: "",
    stad: "",
    postcode: "",
    email: "",
    telefoonnummer: "",
    functie: ""        
  });



  useEffect(() => {
  const fetchNurseDetails = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API_BASE}/nurses/${id}/details`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) return alert(data.message || "Not Found");

      setRol(data.role || "Verpleegkundige");

      setFormData({
        voornaam: data.voornaam || "",
        naam: data.naam || "",
        afkorting: data.afkorting || "",
        straatnaam: data.straatnaam || "",
        nummer: data.nummer || "",
        stad: data.stad || "",
        postcode: data.postcode || "",
        email: data.email || "",
        telefoonnummer: data.telefoonnummer || "",
        functie: data.functie || ""    
      });

      document.title = `${data.voornaam} ${data.naam} - Portaal`;
    } catch (error) {
      console.error(error);
    }
  };

  if (id) fetchNurseDetails();
}, [id]);


  const handleBack = () => {
    navigate('/verpleegkundigen');
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
  try {
    const token = localStorage.getItem("auth_token");

    const response = await fetch(`${API_BASE}/nurses/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        ...formData,
        rol,
        functie: formData.functie   
      })
    });

    const data = await response.json();
    if (!response.ok) return alert(data.message || "Fout bij opslaan");

    alert("Verpleegkundige succesvol opgeslagen!");
    navigate("/verpleegkundigen");
  } catch (error) {
    console.error(error);
    alert("Kon verpleegkundige niet opslaan");
  }
};

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("auth_token");

      const response = await fetch(`${API_BASE}/nurses/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) return alert(data.message || "Fout bij verwijderen");

      alert("Verpleegkundige succesvol verwijderd!");
      navigate("/verpleegkundigen");
    } catch (error) {
      console.error(error);
      alert("Kon verpleegkundige niet verwijderen");
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar open={true} onClose={() => {}} />
      
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          ml: { xs: 0, md: '70px' },
          mt: { xs: '60px', md: 0 },
          p: { xs: 1, sm: 2, md: 3 }
        }}
      >
        <Container maxWidth="lg">
          {/* Header with back button and delete button */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={handleBack}
                sx={{
                  mr: 2,
                  color: '#4aa8b4',
                  '&:hover': {
                    backgroundColor: 'rgba(74, 168, 180, 0.1)'
                  }
                }}
              >
                <ArrowBack />
              </IconButton>
              <Typography variant="h4" sx={{ color: '#4aa8b4' }}>
                Terug
              </Typography>
            </Box>
            
            {/* Delete button - alleen voor admin */}
            {user?.role === 'admin' && (
              <IconButton
                onClick={handleDeleteClick}
                sx={{
                  color: '#d32f2f',
                  '&:hover': {
                    backgroundColor: 'rgba(211, 47, 47, 0.1)'
                  }
                }}
              >
                <Delete />
              </IconButton>
            )}
          </Box>

          <Paper sx={{ p: 4 }}>
            {/* Rol dropdown */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                Rol
              </Typography>
              <FormControl sx={{ minWidth: 200 }}>
                <Select
                  value={rol}
                  onChange={(e) => setRol(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="nurse">Verpleegkundige</MenuItem>
                  <MenuItem value="admin">admin</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Functie dropdown */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                Functie
              </Typography>
              <FormControl sx={{ minWidth: 200 }}>
                <Select
                  value={formData.functie}
                  onChange={(e) => handleInputChange("functie", e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">Geen functie gekozen</MenuItem>
                  <MenuItem value="Verpleegkundige">Verpleegkundige</MenuItem>
                  <MenuItem value="Zorgkundige">Zorgkundige</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Naam section */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Voornaam
                </Typography>
                <TextField
                  value={formData.voornaam}
                  onChange={(e) => handleInputChange('voornaam', e.target.value)}
                  fullWidth
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Naam
                </Typography>
                <TextField
                  value={formData.naam}
                  onChange={(e) => handleInputChange('naam', e.target.value)}
                  fullWidth
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Afkorting
                </Typography>
                <TextField
                  value={formData.afkorting}
                  onChange={(e) => handleInputChange('afkorting', e.target.value)}
                  fullWidth
                  variant="outlined"
                />
              </Grid>
            </Grid>

            {/* Adres section */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Straatnaam
                </Typography>
                <TextField
                  value={formData.straatnaam}
                  onChange={(e) => handleInputChange('straatnaam', e.target.value)}
                  placeholder="straatnaam"
                  fullWidth
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Nummer
                </Typography>
                <TextField
                  value={formData.nummer}
                  onChange={(e) => handleInputChange('nummer', e.target.value)}
                  placeholder="nummer"
                  fullWidth
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Stad
                </Typography>
                <TextField
                  value={formData.stad}
                  onChange={(e) => handleInputChange('stad', e.target.value)}
                  placeholder="stad"
                  fullWidth
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Postcode
                </Typography>
                <TextField
                  value={formData.postcode}
                  onChange={(e) => handleInputChange('postcode', e.target.value)}
                  placeholder="postcode"
                  fullWidth
                  variant="outlined"
                />
              </Grid>
            </Grid>

            {/* Contact section */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Email
                </Typography>
                <TextField
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  fullWidth
                  variant="outlined"
                  type="email"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Telefoonnummer
                </Typography>
                <TextField
                  value={formData.telefoonnummer}
                  onChange={(e) => handleInputChange('telefoonnummer', e.target.value)}
                  fullWidth
                  variant="outlined"
                />
              </Grid>
            </Grid>

            {/* Save buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                sx={{
                  borderColor: '#4aa8b4',
                  color: '#4aa8b4',
                  '&:hover': {
                    borderColor: '#3e8892',
                    backgroundColor: 'rgba(74, 168, 180, 0.1)'
                  }
                }}
              >
                Annuleren
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{
                  backgroundColor: '#4aa8b4',
                  '&:hover': {
                    backgroundColor: '#3e8892'
                  }
                }}
              >
                Opslaan
              </Button>
            </Box>
          </Paper>

          {/* Delete confirmation dialog */}
          <Dialog
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
            aria-labelledby="delete-dialog-title"
            aria-describedby="delete-dialog-description"
          >
            <DialogTitle id="delete-dialog-title">
              Verpleegkundige verwijderen
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="delete-dialog-description">
                Weet je zeker dat je "<strong>{formData.voornaam} {formData.naam}</strong>" wilt verwijderen? 
                Deze actie kan niet ongedaan worden gemaakt.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={() => setDeleteDialogOpen(false)}
                sx={{ color: '#666' }}
              >
                Annuleren
              </Button>
              <Button 
                onClick={handleDelete} 
                variant="contained"
                sx={{
                  backgroundColor: '#d32f2f',
                  '&:hover': {
                    backgroundColor: '#b71c1c'
                  }
                }}
              >
                Verwijderen
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Box>
    </Box>
  );
};

export default VerpleegkundigeDetail;